import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from 'notistack';
import { ItemListRequest, ItemListResponse } from "helpers/interfaces";
import { Redirect, useHistory, useLocation } from "react-router";


interface IUsersContext {
    recordCount: number
    search: ISearch
    users: IUser[]
    isLoading: boolean

    updateSearch: (newSearch: ISearch) => void
    doSearch: (newSearch: ISearch) => void
    changePage: (page: number) => void
    deleteUser: (UserId: string) => Promise<void>
}

export interface IUser {
    id: string
    email: string
    firstName: string
    lastName: string
    groups: string[]
    defaultSupplier : string
}

export let UsersContext = React.createContext<IUsersContext>({} as IUsersContext);

let { Provider } = UsersContext;

interface ISearch extends ItemListRequest {
    text: string
    group: string
    canWorkOnAllTickets: string
    canCreateTicket: string
    canDeleteTicket: string
    canReplyToCustomers: string
}

let UsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let history = useHistory();

    let [recordCount, setRecordCount] = useState<number>(0);
    let [search, setSearch] = useState<ISearch>({
        group: "",
        pageNr: 0,
        pageSize: 20,
        text: "",
        canCreateTicket: "",
        canDeleteTicket: "",
        canReplyToCustomers: "",
        canWorkOnAllTickets: ""
    });

    let [isLoading, setLoading] = useState(true);
    let [users, setUsers] = useState<IUser[]>([]);


    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        let request = { ...search }

        if (state.search) {
            request = {
                ...request,
                ...state.search
            }
        }

        setSearch(request)
        searchUsers(request);
    }

    let doSearch = (newSearch: ISearch) => {
        let request = {
            ...newSearch,
            pageNr: 0
        }

        history.push(location.pathname, { search: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchUsers = async (searchRequest: ISearch) => {
        try {
            setLoading(true);

            let response = await axios.post<ItemListResponse<IUser>>('/api/User/SearchUsers', searchRequest);
            setUsers(response.data.items);
            setRecordCount(response.data.recordCount);
            
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let changePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let deleteUser = async (UserId: string) => {
        if (window.confirm("Are you sure that you want to remove this user?")) {
            try {
                await axios.delete(`/api/User/DeleteUser/${UserId}`);
                setUsers(users.filter(user => user.id !== UserId));
                enqueueSnackbar("User deleted", { variant: 'success' });
            } catch (err) { }
        }
    }

    return (
        <Provider value={{
            recordCount,
            search,
            users,
            isLoading,

            changePage,
            doSearch,
            updateSearch,
            deleteUser
        }}>
            {children}
        </Provider>
    );
};

export default UsersProvider